// Only cPanel-backed services can hold mailboxes.
export const MAILBOX_SERVICE_TYPE = 'cpanel'

// Service statuses whose mailboxes we surface. Anything else (Cancelled,
// Fraud, Terminated) has no reachable mail.
export const MAILBOX_SERVICE_STATUSES = ['Active', 'Pending']

// `Email::list_pops_with_disk` returns this literal string in `humandiskquota`
// instead of a size when the mailbox quota is unlimited. cPanel's own docs
// describe `∞` and `unlimited` for that case, so do not trust either — this is
// the value the API actually returns to us. See docs/amapi-email.md § 1.6.
export const UNLIMITED_QUOTA = 'None'

// A mailbox's own status, which is not the status of the service hosting it
// (AM-1104, Kaumil 2026-09-22: "Ignore pending setup. Likely will be suspended
// only or whatever cPanel has for statuses for email accounts").
//
// These strings are not free-form: `statusColor` (helpers.js:202) matches
// them against its own tables — 'Active' is in `greenValues` and 'Suspended' in
// `redValues` — so the status dot and chip colour correctly with no new helper.
// Renaming either one silently turns the dot orange. 'On Hold' is in no table on
// purpose: it falls through to orange, the warning colour it should have.
export const MAILBOX_STATUS_ACTIVE = 'Active'
export const MAILBOX_STATUS_ON_HOLD = 'On Hold'
export const MAILBOX_STATUS_SUSPENDED = 'Suspended'

// Values of a mailbox's restriction settings, as the Security tab offers them.
// Mirrors cPanel's own Manage screen: incoming mail and login are Allow or
// Suspend, outgoing mail is Allow, Suspend or Hold (AM-1104, Kaumil 2026-10-01).
export const RESTRICTION_ALLOW = 'allow'
export const RESTRICTION_SUSPEND = 'suspend'
export const RESTRICTION_HOLD = 'hold'

// Quota, in MB, every new mailbox is created with. `Email::add_pop` takes
// `quota` in MB. Fixed rather than chosen by the customer, and not changeable
// afterwards (AM-1109: "they will only be able to create a 1 GB email account").
export const MAILBOX_QUOTA_MB = 1024

// How many services we query in parallel during the cross-service fan-out.
// One request per service per resource, each proxied through apirouter to
// cPanel, so keep this low.
export const FANOUT_CONCURRENCY = 3

/**
 * Path to a section of an email account, addressed by its main domain.
 *
 * Built as an encoded path string rather than `{ name, params }` on purpose:
 * vue-router 3 encodes `params` itself, so a value that has already been
 * through `encodeURIComponent` comes back double-encoded (`@` -> `%2540`).
 * Routing every link into these routes through the builders here keeps that
 * from happening, and keeps the encoding rule in a single place.
 */
export const accountPath = (mainDomain, section = '', item = '') => {
  let path = `/email/${encodeURIComponent(mainDomain)}`
  if (section) path += `/${section}`
  // One mailbox, filtered address or domain within the section.
  if (section && item) path += `/${encodeURIComponent(item)}`
  return path
}

/**
 * The empty state of each email feature (AM-1107): what shows instead of an
 * empty table or form when nothing is set up yet. `learnMore` is optional —
 * the ticket gives no article for aliases.
 */
export const EMAIL_FEATURE_SETUP = {
  accounts: {
    heading: 'Accounts',
    icon: 'mdi-email-plus-outline',
    title: 'Create your first mailbox',
    description: 'Mailboxes give you an email address on your own domain, with webmail and email app access',
    action: 'Create mailbox',
    learnMore: 'https://www.greengeeks.com/support/article/how-do-i-setup-an-e-mail-account/',
  },
  aliases: {
    heading: 'Aliases',
    icon: 'mdi-at',
    title: 'Create your first alias',
    description: 'Aliases deliver email sent to another address straight into this mailbox',
    action: 'Create alias',
    learnMore: '',
  },
  forwarders: {
    heading: 'Forwarders',
    icon: 'mdi-email-send-outline',
    title: 'Create your first forwarder',
    description: 'Email forwarders redirect incoming email messages to another mailbox',
    action: 'Create forwarder',
    learnMore: 'https://www.greengeeks.com/support/article/getting-started-setting-up-email-forwarding/',
  },
  autoresponders: {
    heading: 'Autoresponders',
    icon: 'mdi-reply-outline',
    title: 'Create your first autoresponder',
    description: 'Autoresponders send an automatic reply to incoming email messages, for example while you are away',
    action: 'Create autoresponder',
    learnMore: 'https://www.greengeeks.com/support/article/getting-started-cpanel-create-email-autoresponders/',
  },
  filters: {
    heading: 'Filters',
    icon: 'mdi-filter-outline',
    title: 'Create your first filter',
    description: 'Email filters sort, forward or discard incoming email messages based on rules you set',
    action: 'Create filter',
    learnMore: 'https://www.greengeeks.com/tutorials/how-to-setup-email-user-and-account-filtering/',
  },
}

/**
 * Whether a mailbox's domain belongs to a domain shown as its own account.
 *
 * An addon domain is listed and managed apart from its main domain (AM-1104,
 * 2026-10-07), so each takes its own subdomains, and the main domain takes
 * whatever is left — itself, its subdomains, and anything not under an addon.
 */
export const domainInScope = (domain, scope, addonDomains) => {
  const name = String(domain || '').toLowerCase()
  const under = (parent) => {
    const root = String(parent || '').toLowerCase()
    return name === root || name.endsWith(`.${root}`)
  }
  const target = String(scope || '').toLowerCase()
  const addons = addonDomains.map(addon => String(addon).toLowerCase())
  if (addons.includes(target)) return under(target)
  return !addons.some(under)
}
